import React, { createRef, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { createStructuredSelector } from 'reselect';

// Global Types
import { AppState } from 'services/types';
import {
    VirtualizedTableProps,
    VirtualizedTableItemValue,
    VirtualizedTableErrorMessage
} from 'services/virtualizedTable/types';

// Global Selectors
import {
    makeSelectVirtualizedTableItemValue,
    makeSelectVirtualizedTableErrorMessage
} from 'services/virtualizedTable/selectors';

import { setItemValue } from 'services/virtualizedTable/actions';

import { VirtualizedTableOnItemValueChange } from '../../types';
import './styles.scss';

const AsyncDatePicker = lazy(() => import('react-datepicker'));

interface OwnProps extends VirtualizedTableProps {
    onItemValueChange?: VirtualizedTableOnItemValueChange;
}

interface ConnectedProps {
    itemValue?: VirtualizedTableItemValue;
    errorMessage?: VirtualizedTableErrorMessage;
}

interface DispatchProps {
    setItemValue: typeof setItemValue;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

interface State {
    initialItemValue: VirtualizedTableItemValue;
}

export class VirtualizedTableDatePickerComponent extends React.PureComponent<
    Props,
    State
> {
    // TODO: when TS is 3.8, solve this issue with import type ReactDatePicker
    datePickerEl = createRef<any>();

    constructor(props: Props) {
        super(props);

        this.state = {
            initialItemValue: props.itemValue
        };
    }

    componentWillUnmount(): void {
        this.updateItemValue();
    }

    handleChange = (date: Date | null): void => {
        const {
            itemKey,
            columnKey,
            itemValue,
            setItemValue: setValue
        } = this.props;
        const newValue = date && moment(date).format('YYYY-MM-DD');

        if (itemKey && columnKey && newValue !== itemValue) {
            setValue(itemKey, columnKey, newValue);
        }
    };

    updateItemValue = (): void => {
        const {
            itemKey,
            columnKey,
            onItemValueChange,
            setItemValue: setValue,
            errorMessage,
            itemValue
        } = this.props;
        const { initialItemValue } = this.state;

        if (itemKey && columnKey && itemValue !== initialItemValue) {
            if (errorMessage) {
                setValue(itemKey, columnKey, initialItemValue);
            } else if (onItemValueChange) {
                onItemValueChange(itemKey, columnKey, itemValue);
            }
        }
    };

    handleBlur = (): void => {
        if (this.datePickerEl.current) {
            this.datePickerEl.current.setFocus();
        }
    };

    render(): React.ReactElement {
        const { itemValue } = this.props;
        const selectedDate = itemValue ? new Date(itemValue) : undefined;
        const popperModifiers = [
            {
                name: 'offset',
                options: {
                    offset: [-9, 0]
                }
            }
        ];

        return (
            <Suspense fallback={null}>
                <AsyncDatePicker
                    ref={this.datePickerEl}
                    dateFormat="dd/MM/yyyy"
                    popperClassName="entity-value-update-date-popper"
                    popperModifiers={popperModifiers}
                    selected={selectedDate}
                    autoFocus
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                />
            </Suspense>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    itemValue: makeSelectVirtualizedTableItemValue(),
    errorMessage: makeSelectVirtualizedTableErrorMessage()
});

export default connect(mapStateToProps, {
    setItemValue
})(VirtualizedTableDatePickerComponent);
